import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CircleUserRound, Eye, TriangleAlert } from "lucide-react";
import type {
  CampaignRecipientListItem,
  WhatsAppTemplatePresentation,
} from "@multi-chat/shared";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import { WhatsAppTemplatePreview } from "../whatsapp/WhatsAppTemplatePreview/index.js";
import type { WhatsAppTemplatePreviewProps } from "../whatsapp/WhatsAppTemplatePreview/WhatsAppTemplatePreview.js";
import { CampaignComboSelect } from "./CampaignComboSelect.js";
import { campaignLocaleAlignClass, recipientDisplayName } from "./campaigns-presentation.js";

const recipientPillClass =
  "group inline-flex h-8 min-w-0 max-w-full items-center gap-1.5 rounded-full border border-line bg-surface px-3 text-[0.75rem] shadow-[0_1px_2px_rgb(15_23_42/0.04)] transition-[border-color,background-color,box-shadow] hover:border-line-strong hover:bg-surface-muted/60 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)] data-[state=open]:border-line-strong data-[state=open]:bg-surface-muted/60";

function filterRecipients(
  items: readonly CampaignRecipientListItem[],
  query: string,
): CampaignRecipientListItem[] {
  const needle = query.trim().toLowerCase();
  const digits = needle.replace(/[^\d]/g, "");
  return items.filter(
    (item) =>
      recipientDisplayName(item).toLowerCase().includes(needle) ||
      (digits.length > 0 && item.normalizedPhoneE164.includes(digits)),
  );
}

/** Name over phone, aligned by UI locale; the phone keeps LTR digit order. */
function RecipientIdentity({
  recipient,
  uiDir,
}: {
  recipient: CampaignRecipientListItem;
  uiDir: string;
}): React.JSX.Element {
  return (
    <span className={cn("grid min-w-0 flex-1 gap-0.5", campaignLocaleAlignClass(uiDir))}>
      <span dir={uiDir} className="block w-full truncate text-[0.8125rem] font-semibold text-ink">
        {recipientDisplayName(recipient)}
      </span>
      <span className="block w-full truncate text-[0.7rem] text-muted">
        <LtrValue className="tabular-nums">{recipient.normalizedPhoneE164}</LtrValue>
      </span>
    </span>
  );
}

/** Compact searchable preview-recipient pill, rendered in the Content preview toolbar. */
export function CampaignPreviewRecipientPicker({
  recipients,
  previewRecipientId,
  onPreviewRecipientChange,
  className,
  testId,
}: {
  recipients: readonly CampaignRecipientListItem[];
  previewRecipientId: string | null;
  onPreviewRecipientChange: (id: string | null) => void;
  className?: string;
  testId?: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const uiDir = i18n.dir();
  const recipientHeading = t("campaigns.message.previewAs");
  const previewRecipient =
    previewRecipientId != null
      ? (recipients.find((item) => item.id === previewRecipientId) ?? null)
      : null;

  if (recipients.length === 0) {
    return (
      <p className={cn("m-0 text-[0.75rem] text-muted", className)} data-testid={testId}>
        {t("campaigns.message.previewNeedsRecipients")}
      </p>
    );
  }

  return (
    <div className={cn("flex min-w-0", className)} data-testid={testId}>
      <CampaignComboSelect
        items={recipients}
        getKey={(item) => item.id}
        isSelected={(item) => item.id === previewRecipientId}
        onSelect={(item) => onPreviewRecipientChange(item.id)}
        filter={filterRecipients}
        searchPlaceholder={t("campaigns.content.searchRecipients")}
        noMatchesLabel={t("campaigns.content.noRecipientMatches")}
        ariaLabel={
          previewRecipient
            ? `${recipientHeading}: ${recipientDisplayName(previewRecipient)}`
            : recipientHeading
        }
        align="end"
        triggerClassName={recipientPillClass}
        testId="campaign-preview-recipient-select"
        menuTestId="campaign-preview-recipient-menu"
        searchTestId="campaign-preview-recipient-search"
        optionTestId={(item) => `campaign-preview-recipient-option-${item.id}`}
        renderTrigger={() => (
          <>
            <CircleUserRound size={15} className="shrink-0 text-muted" aria-hidden="true" />
            {previewRecipient ? (
              <span dir={uiDir} className="min-w-0 truncate">
                <span className="font-semibold text-ink">
                  {recipientDisplayName(previewRecipient)}
                </span>
                <span className="text-subtle" aria-hidden="true">
                  {" · "}
                </span>
                <LtrValue className="tabular-nums text-muted">
                  {previewRecipient.normalizedPhoneE164}
                </LtrValue>
              </span>
            ) : (
              <span className="min-w-0 truncate font-medium text-muted">{recipientHeading}</span>
            )}
          </>
        )}
        renderOption={(item) => <RecipientIdentity recipient={item} uiDir={uiDir} />}
      />
    </div>
  );
}

export function CampaignLivePreview({
  recipients,
  previewRecipientId,
  onPreviewRecipientChange,
  presentation,
  language,
  missingLabels,
  recipientSelectTestId,
  sectionTestId = "campaign-live-preview",
  previewTestId = "campaign-message-preview",
  title,
  description,
  recipientLabel,
  textSlots,
  renderButtonAccessory,
  renderFrame,
  workspace = false,
}: {
  recipients: readonly CampaignRecipientListItem[];
  previewRecipientId: string | null;
  onPreviewRecipientChange: (id: string | null) => void;
  presentation: WhatsAppTemplatePresentation | null;
  language: string | null;
  missingLabels: readonly string[];
  recipientSelectTestId?: string;
  sectionTestId?: string;
  previewTestId?: string;
  title?: string;
  description?: string;
  recipientLabel?: string;
  textSlots?: WhatsAppTemplatePreviewProps["textSlots"];
  renderButtonAccessory?: WhatsAppTemplatePreviewProps["renderButtonAccessory"];
  /** Workspace mode only: wraps the preview (e.g. a device frame); the recipient picker is rendered by the host. */
  renderFrame?: (preview: ReactNode) => ReactNode;
  workspace?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const uiDir = i18n.dir();
  const heading = title ?? t("campaigns.message.preview");
  const helper = description ?? t("campaigns.message.previewDescription");
  const recipientHeading = recipientLabel ?? t("campaigns.message.previewAs");
  const previewRecipient =
    previewRecipientId != null
      ? (recipients.find((item) => item.id === previewRecipientId) ?? null)
      : null;

  const recipientPicker =
    recipients.length > 0 ? (
      <div className="grid gap-1.5" data-testid={recipientSelectTestId}>
        <p className="m-0 text-[0.75rem] font-semibold text-ink-muted">{recipientHeading}</p>
        <CampaignComboSelect
          items={recipients}
          getKey={(item) => item.id}
          isSelected={(item) => item.id === previewRecipientId}
          onSelect={(item) => onPreviewRecipientChange(item.id)}
          filter={filterRecipients}
          searchPlaceholder={t("campaigns.content.searchRecipients")}
          noMatchesLabel={t("campaigns.content.noRecipientMatches")}
          ariaLabel={recipientHeading}
          testId="campaign-preview-recipient-select"
          menuTestId="campaign-preview-recipient-menu"
          searchTestId="campaign-preview-recipient-search"
          optionTestId={(item) => `campaign-preview-recipient-option-${item.id}`}
          renderTrigger={() => (
            <>
              <CircleUserRound size={20} className="shrink-0 text-subtle" aria-hidden="true" />
              {previewRecipient ? (
                <RecipientIdentity recipient={previewRecipient} uiDir={uiDir} />
              ) : (
                <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-subtle">
                  {recipientHeading}
                </span>
              )}
            </>
          )}
          renderOption={(item) => <RecipientIdentity recipient={item} uiDir={uiDir} />}
        />
      </div>
    ) : (
      <p className="m-0 text-[0.8125rem] text-muted">
        {t("campaigns.message.previewNeedsRecipients")}
      </p>
    );

  const missingWarning =
    missingLabels.length > 0 ? (
      <div
        className="grid gap-0.5 rounded-md border border-warning/30 bg-warning-muted px-2.5 py-2"
        role="status"
      >
        <p className="m-0 flex items-start gap-1.5 text-[0.75rem] font-medium text-warning">
          <TriangleAlert size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
          {t("campaigns.message.previewIncomplete")}
        </p>
        <p className="m-0 ps-5 text-[0.75rem] leading-snug text-warning">
          {t("campaigns.message.previewIncompleteNamed", {
            label: missingLabels[0],
          })}
        </p>
      </div>
    ) : null;

  if (workspace && renderFrame) {
    return (
      <section className="grid min-w-0 gap-3" data-testid={sectionTestId}>
        <div className="grid min-w-0 gap-3" data-testid={presentation ? previewTestId : undefined}>
          {renderFrame(
            presentation ? (
              <WhatsAppTemplatePreview
                presentation={presentation}
                contentLanguage={language}
                textSlots={textSlots}
                renderButtonAccessory={renderButtonAccessory}
              />
            ) : (
              <p className="m-0 rounded-md bg-white/85 px-3 py-6 text-center text-[0.8125rem] text-muted">
                {t("campaigns.message.previewEmpty")}
              </p>
            ),
          )}
          {presentation ? missingWarning : null}
        </div>
      </section>
    );
  }

  return (
    <section
      className={
        workspace
          ? "grid gap-3"
          : "grid gap-3 rounded-xl border border-line bg-surface p-4 shadow-sm"
      }
      data-testid={sectionTestId}
    >
      <div className="grid gap-0.5">
        <h2 className="m-0 flex items-center gap-2 text-sm font-semibold text-ink">
          <Eye size={14} aria-hidden="true" />
          {heading}
        </h2>
        <p className="m-0 text-xs leading-snug text-ink-muted">
          {helper}
        </p>
      </div>

      {recipientPicker}

      {presentation ? (
        <div className="grid gap-2" data-testid={previewTestId}>
          {missingWarning}
          <div
            className={
              workspace
                ? "grid justify-items-stretch"
                : "grid min-h-80 content-center justify-items-center rounded-2xl border border-line bg-[color-mix(in_srgb,var(--color-surface-muted)_88%,var(--color-accent)_12%)] px-4 py-8"
            }
          >
            <div className={workspace ? "w-full" : "w-full max-w-[18rem]"}>
              <WhatsAppTemplatePreview
                presentation={presentation}
                contentLanguage={language}
                textSlots={textSlots}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid min-h-80 content-center justify-items-center rounded-2xl border border-dashed border-line px-4 py-8">
          <p className="m-0 text-center text-[0.8125rem] text-muted">
            {t("campaigns.message.previewEmpty")}
          </p>
        </div>
      )}
    </section>
  );
}
